import { push } from 'redux-first-history';
import { all, takeEvery, put, call } from 'redux-saga/effects';
import actions from './actions';
import Api, { executeAction, resetPassword } from '../../../helpers/api';
import { sendRequest } from '../../../helpers/saga';
import { logger } from '../../../helpers/logger';
import notifications from '../../../redux/notifications/actions';
import { keyByProperty } from '../../../helpers/core';
import { isAUS } from '../../../helpers/countriesHelpers';
import { WorkerStatuses } from '../../../helpers/workerStatuses';
import { t } from '../../../i18n';

const api = new Api('applicants/:id');
const applicantsListApi = new Api('applicants');
const statesApi = new Api('states/list?pageSize=99');
const migrateToJJJAPI = new Api('applicants/:id/transfer_data_from_bench');

const convertTimings = timings => {
  const addSkippedProperty = stages => {
    return stages.map((itemStage, index, elements) => {
      const allNextElements = elements.slice(index);

      return {
        ...itemStage,
        skipped: itemStage.time === null && allNextElements.some(item => item.time),
      };
    });
  };

  const flatStagesArray =
    timings &&
    timings.length > 0 &&
    timings.reduce((acc, item) => {
      acc.push(...item.stages);

      return acc;
    }, []);

  const stagesWithSkippedProperty = addSkippedProperty(flatStagesArray);

  let index = 0;
  const newTimings = timings.map(element => {
    const newEl = { ...element, stages: stagesWithSkippedProperty.slice(index, index + element.stages.length) };
    index += element.stages.length;

    return newEl;
  });

  return newTimings;
};

function convertSkills(skills) {
  if (!skills) {
    return [];
  }

  return skills.map(skill => {
    if (!skill.position) {
      return skill;
    }
    const { position, ...rest } = skill;

    return { position_id: position.id, position_name: position.name, ...rest };
  });
}

function* loadData({ payload }) {
  try {
    const { id, isEdit } = payload;

    const { result, formInfo, states } = yield all({
      result: call(sendRequest, api.load, { id }, notifications.REQUEST_ERROR, null, true),
      formInfo: call(sendRequest, api.loadFormData, { id: payload.id }, notifications.REQUEST_ERROR),
      states: call(sendRequest, statesApi.load, {}, notifications.REQUEST_ERROR),
    });

    if (result && formInfo && states) {
      const { data } = result;

      yield put(
        actions.loadFormData({
          data: formInfo.data,
          states: states.data.data.map(item => ({ name: item.code, id: item.id })),
        }),
      );

      yield put(actions.loadDataSuccess({ data: { ...data, timings: convertTimings(data.timings), id } }));

      if (isEdit) {
        data.skills = convertSkills(data.skills);
        yield put(actions.edit({ id, applicant: { ...data, address: '' } }));
      }
    }
  } catch (error) {
    logger.error(error);
  }
}

function* updateApplicant(data) {
  try {
    const { payload } = data;
    const { id, editForm, hidden_fields, isInterviewsView, sickAndLeaveRadio } = payload;
    const propsArray = Object.values(editForm);
    const applicant = keyByProperty(propsArray, 'name', prop => prop.value);
    const competencies_attributes = applicant.skills.map(item => {
      return {
        ...item,
        position_id: item.position_id,
        level: item.level ? item.level.id : undefined,
        id: item.newSkill ? null : item.id,
      };
    });

    applicant.birthday = applicant.birthday ? applicant.birthday.slice(0, 10) : null;
    hidden_fields.forEach(item => delete applicant[item]);

    applicant.sick_hours_attributes = applicant.sick_hours;
    delete applicant.sick_hours;

    if (applicant.status === WorkerStatuses.HIRED) {
      delete applicant.sick_hours_attributes;
    }

    if (sickAndLeaveRadio === 'No') {
      delete applicant.sick_hours_attributes;
      delete applicant.pto_hours_accrued;
    }

    applicant.tax_information_attributes = applicant.tax_information?.tax_file_number
      ? {
          id: applicant.tax_information.id,
          tax_file_number: applicant.tax_information.tax_file_number.replace(/ /g, ''),
        }
      : null;
    delete applicant.tax_information;

    const result = yield call(
      sendRequest,
      api.updateItem,
      {
        data: {
          applicant: {
            ...applicant,
            skills: isAUS() ? undefined : applicant.skills,
            competencies_attributes,
            country_id: applicant.country,
          },
        },
        id,
      },
      notifications.REQUEST_ERROR,
    );

    if (result) {
      if (result.data) {
        const { skills } = applicant;
        const outputSkills = skills
          .map(item => {
            // eslint-disable-next-line no-underscore-dangle
            if (item._destroy) {
              return null;
            }

            return item;
          })
          .filter(item => {
            return item !== null;
          });

        yield put(actions.saveSuccess({ data: { ...applicant, skills: outputSkills } }));

        yield put(
          notifications.addNotification({
            headline: t('dash.common.notification.success_headline'),
            type: 'success',
            message: t('dash.common.notification.updated_successfully', { item: t('dash.applicants.applicant_title') }),
          }),
        );

        if (isInterviewsView) {
          yield put(push(`/interviews/${id}`));
        } else {
          yield put(push(`/applicants/${id}?tab[]=details`));
        }
      } else {
        yield put(
          notifications.addNotification({
            headline: t('dash.common.notification.error_headline'),
            type: 'error',
            message: t('dash.common.notification.check_validation_errors'),
          }),
        );
        yield put(actions.saveError({ data: result }));
      }
    }
  } catch (error) {
    logger.error(error);
  }
}

function* deleteApplicant(data) {
  try {
    const { payload } = data;
    const { id } = payload;

    const result = yield call(sendRequest, api.deleteItem, { id }, notifications.REQUEST_ERROR);

    if (result && result.data) {
      yield put(actions.deleteSuccess());
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.deleted_successfully', { item: t('dash.applicants.applicant_title') }),
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* addApplicant(data) {
  try {
    const { payload } = data;
    const { editForm, sickAndLeaveRadio } = payload;

    const propsArray = Object.values(editForm);
    const applicant = keyByProperty(propsArray, 'name', prop => prop.value);

    const competencies_attributes = applicant.skills.map(item => {
      return {
        position_name: item.position_name,
        position_id: item.position_id,
        id: null,
      };
    });

    applicant.sick_hours_attributes = applicant.sick_hours;
    delete applicant.sick_hours;

    if (sickAndLeaveRadio === 'No') {
      delete applicant.sick_hours_attributes;
      delete applicant.pto_hours_accrued;
    }

    applicant.tax_information_attributes =
      applicant.tax_information && applicant.tax_information.tax_file_number
        ? {
            id: null,
            tax_file_number: applicant.tax_information.tax_file_number.replace(/ /g, ''),
          }
        : null;
    delete applicant.tax_information;

    const { birthday, skills: _skills, ...otherProps } = applicant;

    const result = yield call(
      sendRequest,
      applicantsListApi.createItem,
      {
        data: {
          applicant: {
            ...otherProps,
            skills: isAUS() ? undefined : applicant.skills,
            competencies_attributes,
            birthday: birthday ? birthday.slice(0, 10) : birthday,
            country_id: applicant.country,
          },
        },
      },
      notifications.REQUEST_ERROR,
    );

    if (result) {
      if (result.data) {
        const { id } = result.data;
        yield put(push(`/applicants/${id}`));
        yield put(actions.saveSuccess());
        yield loadData({ payload: { id } });
        yield put(
          notifications.addNotification({
            headline: t('dash.common.notification.success_headline'),
            type: 'success',
            message: t('dash.common.notification.added_successfully', { item: t('dash.applicants.applicant_title') }),
          }),
        );
      } else {
        yield put(actions.saveError({ data: result }));
      }
    }
  } catch (error) {
    logger.error(error);
  }
}

function* loadFormDataOnAdd() {
  try {
    const { formData, states } = yield all({
      formData: call(sendRequest, applicantsListApi.loadFormData, {}, notifications.REQUEST_ERROR),
      states: call(sendRequest, statesApi.load, {}, notifications.REQUEST_ERROR),
    });

    if (formData && states) {
      yield put(
        actions.loadFormData({
          data: formData.data,
          states: states.data.data.map(item => ({ name: item.code, id: item.id })),
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* resetApplicantPassword({ payload }) {
  try {
    const { id } = payload;

    const result = yield call(sendRequest, resetPassword, { id, path: 'applicants/:id' }, notifications.REQUEST_ERROR);

    if (result && result.data) {
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.applicants.password_reset_link_emailed_notification'),
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* sendApplicantRequest({ payload }) {
  try {
    const { url, method, id } = payload;

    const result = yield call(sendRequest, executeAction, { url, method }, notifications.REQUEST_ERROR);

    if (result && result.messages) {
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: result.messages.join(' '),
        }),
      );

      if (id) {
        yield loadData({ payload: { id } });
      }
      yield put(actions.sendRequestSuccess());
    }
  } catch (error) {
    yield put(actions.sendRequestError());
    logger.error(error);
  }
}

function* changeStatus({ payload }) {
  try {
    const { method, url, id, data } = payload;

    const result = yield call(sendRequest, executeAction, { url, method, data }, notifications.REQUEST_ERROR);

    if (result && result.data) {
      yield put(actions.loadData({ id }));
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.has_been_changed', { item: t('dash.talent.status_label') }),
        }),
      );
    } else {
      const errors = Object.keys(result).flatMap(errorType => result[errorType]);

      yield all(
        errors.map(message =>
          put(
            notifications.addNotification({
              headline: t('dash.common.notification.error_headline'),
              type: 'error',
              message,
            }),
          ),
        ),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* migrateToJJJ({ payload }) {
  try {
    const { id } = payload;

    const result = yield call(
      sendRequest,
      migrateToJJJAPI.createItem,
      {
        id,
      },
      notifications.REQUEST_ERROR,
    );

    if (result?.status === 200) {
      yield loadData({ payload: { id } });
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.applicants.migrate_to_jjj.migration_done_successfully_message'),
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

export {
  addApplicant,
  changeStatus,
  deleteApplicant,
  loadData,
  loadFormDataOnAdd,
  resetApplicantPassword,
  sendApplicantRequest,
  updateApplicant,
  migrateToJJJ,
};
export default function* rootSaga() {
  yield all([
    takeEvery(actions.ADD, addApplicant),
    takeEvery(actions.CHANGE_STATUS, changeStatus),
    takeEvery(actions.CREATE, loadFormDataOnAdd),
    takeEvery(actions.DELETE, deleteApplicant),
    takeEvery(actions.LOAD, loadData),
    takeEvery(actions.RESET_PASSWORD, resetApplicantPassword),
    takeEvery(actions.SAVE, updateApplicant),
    takeEvery(actions.SEND_REQUEST, sendApplicantRequest),
    takeEvery(actions.MIGRATE_TO_DFW_REQUEST, migrateToJJJ),
  ]);
}
